import { useCallback, useEffect, useRef } from "react";

// Keep each side-band label visible while the grid scrolls sideways.
//
// A band label is centred over its whole span, but the insurer band can be 27
// columns / several thousand px wide — scroll to either end of it and the label
// sits off-screen, leaving a bare coloured bar with no name on it. That is the
// same failure the per-column prefixes had (the identifying token not being where
// the reader is looking), just moved up a row.
//
// So the label tracks the viewport: it sits at the centre of the band's VISIBLE
// portion, clamped to the band's own edges so it can never drift over columns it
// doesn't name. A band fully in view is unaffected — its visible centre is its
// real centre — so the common narrow-band case looks exactly as before.
//
// Performance: this runs on scroll, over grids that can hold tens of thousands of
// rows, so it must not re-render React. It writes `transform` directly on the few
// label nodes (one per band, typically 2-4) inside a rAF, so a burst of scroll
// events collapses into one write per frame and no layout is invalidated —
// transform is compositor-only. Reads (offsetLeft/offsetWidth) are taken once per
// frame BEFORE any write, so the loop never interleaves read/write and thrashes.

/** Marks a label node so the tracker can find it, and its band extent. */
export const BAND_LABEL_ATTR = "data-band-label";

export interface BandLabelTracker {
  /** Attach to the element that scrolls horizontally (the grid's overflow host). */
  register: (el: HTMLDivElement | null) => void;
  /** Call after the column set changes, so extents are re-measured. */
  refresh: () => void;
}

/**
 * @param enabled off for non-paired grids, so they pay nothing
 * @param deps    changes that can move band extents (column set / pins / frame)
 */
export function useBandLabels(enabled: boolean, deps: unknown): BandLabelTracker {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const update = useCallback(() => {
    rafRef.current = null;
    const host = hostRef.current;
    if (!host) return;
    const labels = host.querySelectorAll<HTMLElement>(`[${BAND_LABEL_ATTR}]`);
    if (!labels.length) return;

    // Everything below is in VIEWPORT coordinates. offsetLeft is unusable here: it
    // resolves against the nearest positioned ancestor, which for these sticky
    // header cells is not the scroll container — measured 387px of skew on a real
    // grid, which silently poisons every offset. getBoundingClientRect puts the
    // band, the label and the viewport in one frame of reference.
    const hostBox = host.getBoundingClientRect();
    const viewL = hostBox.left;
    const viewR = hostBox.right;

    // READ pass: gather every geometry value before writing anything.
    const plan: { el: HTMLElement; dx: number }[] = [];
    labels.forEach((el) => {
      const cell = el.parentElement;
      if (!cell) return;
      const cellBox = cell.getBoundingClientRect();
      const bandL = cellBox.left;
      const bandR = cellBox.right;

      // A frozen band travels with the viewport; its label is already where the
      // reader is looking, so leave it centred in its own cell.
      if (el.dataset.bandPinned === "1") { plan.push({ el, dx: 0 }); return; }

      const visL = Math.max(bandL, viewL);
      const visR = Math.min(bandR, viewR);
      if (visR <= visL) { plan.push({ el, dx: 0 }); return; }  // band off-screen

      // Work in absolute label-left coordinates. The rest position is DERIVED, not
      // measured: the label is centred in its band when untransformed. Measuring it
      // instead would mean reading a rect that already contains the current
      // transform, and any error in backing that out compounds every frame.
      const labelW = el.offsetWidth;      // layout width, transform-independent
      const restLeft = bandL + ((bandR - bandL) - labelW) / 2;

      // Ideal: centred in the visible slice. Then confine to the band, so the label
      // never sits over columns it doesn't name.
      let want = (visL + visR) / 2 - labelW / 2;
      want = Math.max(bandL, Math.min(bandR - labelW, want));

      // If the visible slice can hold the label, keep it fully inside that slice
      // too. A slice narrower than the label admits no such position, and the band
      // clamp above already leaves it as close to view as it can legally get.
      if (visR - visL >= labelW) {
        want = Math.max(visL, Math.min(visR - labelW, want));
      }
      plan.push({ el, dx: want - restLeft });
    });

    // WRITE pass.
    for (const { el, dx } of plan) {
      const r = Math.round(dx);
      el.style.transform = r ? `translateX(${r}px)` : "";
    }
  }, []);

  const schedule = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(update);
  }, [update]);

  const register = useCallback((el: HTMLDivElement | null) => { hostRef.current = el; }, []);

  useEffect(() => {
    if (!enabled) return;
    const host = hostRef.current;
    if (!host) return;
    host.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    ro?.observe(host);
    schedule();  // initial placement
    return () => {
      host.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      ro?.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [enabled, schedule, deps]);

  return { register, refresh: schedule };
}
